import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { AppStoreQuery } from '@core/state';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { Subject, Subscription, merge, fromEvent, timer } from 'rxjs';
import { distinctUntilChanged, map, startWith, switchMap, takeUntil, throttleTime } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class IdleLogoutService implements OnDestroy {
    // 1 hour
    private readonly idleMs = 60 * 60 * 1000;

    private readonly destroy$ = new Subject<void>();
    private idleSub: Subscription | null = null;

    constructor(
        private appStoreQuery: AppStoreQuery,
        private authenticationService: AuthenticationService,
        private ngZone: NgZone,
    ) { }

    /**
     * Starts watching for inactivity when the user is authenticated.
     * Safe to call multiple times (idempotent).
     */
    public start(): void {
        // React to token changes so we only run while authenticated.
        this.appStoreQuery
            .selectAuthentication()
            .pipe(
                map((auth) => auth?.token || ''),
                distinctUntilChanged(),
                takeUntil(this.destroy$),
            )
            .subscribe((token) => {
                if (token) {
                    this.enableIdleTimer();
                } else {
                    this.disableIdleTimer();
                }
            });
    }

    private enableIdleTimer(): void {
        if (this.idleSub) return;

        this.ngZone.runOutsideAngular(() => {
            const activity$ = merge(
                fromEvent(document, 'mousemove'),
                fromEvent(document, 'mousedown'),
                fromEvent(document, 'keydown'),
                fromEvent(document, 'touchstart'),
                fromEvent(document, 'scroll'),
                fromEvent(window, 'resize'),
            ).pipe(throttleTime(1000));

            // Each activity resets the timer. If timer completes -> logout.
            this.idleSub = activity$
                .pipe(
                    startWith(null),
                    switchMap(() => timer(this.idleMs)),
                    takeUntil(this.destroy$),
                )
                .subscribe(() => {
                    // Double-check still authenticated before logging out.
                    const token = this.appStoreQuery.getAuthenticationToken();
                    if (!token) return;

                    this.ngZone.run(() => {
                        this.authenticationService.unauthenticate('idle');
                    });
                });
        });
    }

    private disableIdleTimer(): void {
        if (this.idleSub) {
            this.idleSub.unsubscribe();
            this.idleSub = null;
        }
    }

    public ngOnDestroy(): void {
        this.disableIdleTimer();
        this.destroy$.next();
        this.destroy$.complete();
    }
}

